import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import type { ICustomAuthPublicClientApplication } from "@azure/msal-browser/custom-auth";
import { Button } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import { clearAffiliateResolution, resolveAffiliateOnce } from "../../affiliate/affiliateContext";
import { getAffiliateSlug, readCachedAffiliate } from "../../affiliate/affiliateService";
import { authRuntimeConfig } from "../../authConfig";
import { createAffiliateTheme } from "../../affiliate/createAffiliateTheme";
import { ResolvedAffiliate } from "../../affiliate/types";
import { useDocumentBranding } from "../../affiliate/useDocumentBranding";
import { StorageKeys, consumeRedirectError, readStorage } from "../../auth/browserStorage";
import { getMsalInstance, getNativeAuthClient } from "../../auth/msalInstance";
import { NativeAuthView } from "../../auth/native/useNativeAuth";
import {
  RedirectLoopError,
  resetRedirectLoopGuard,
  safeReturnTo,
  startHostedSignIn,
} from "../../auth/redirectFallback";
import { AuthErrorScreen, AuthStatusScreen } from "./components/AuthShell";
import { NativeAuthFlow } from "./NativeAuthFlow";

type GatewayState =
  | { phase: "resolving" }
  | { phase: "native"; affiliate: ResolvedAffiliate; client: ICustomAuthPublicClientApplication }
  | { phase: "redirecting" }
  | { phase: "error"; message: string };

const CONTINUE_BUTTON_DELAY_MS = 8000;

function seedState(): GatewayState {
  const client = getNativeAuthClient();

  // Not after a failed redirect: that load owes the user an error, not a sign-in form.
  if (!client || readStorage("session", StorageKeys.redirectError)) {
    return { phase: "resolving" };
  }

  const cached = readCachedAffiliate(
    window.location.hostname,
    getAffiliateSlug(),
    authRuntimeConfig.brandingCdnBaseUrl
  );

  return cached ? { phase: "native", affiliate: cached, client } : { phase: "resolving" };
}

/**
 * Entry point for every signed-out route. A URL that resolves to an active affiliate with valid
 * branding gets the branded native sign-in UI; anything else (no affiliate, lookup or CDN failure,
 * native auth disabled) goes to the Microsoft-hosted page. Every phase renders visible content.
 */
export default function SignInGateway({ view }: { view: NativeAuthView }) {
  const { instance, inProgress } = useMsal();
  const baseTheme = useTheme();
  // Seeded from the last known branding for this host and slug, so a returning visitor gets the
  // branded form on the first frame instead of a spinner. The lookup below confirms it.
  const [state, setState] = useState<GatewayState>(seedState);
  const [attempt, setAttempt] = useState(0);
  const [showContinue, setShowContinue] = useState(false);
  // Captured before the native flow rewrites the address bar to /login.
  const [returnTo] = useState(() => safeReturnTo());

  const redirectToHostedSignIn = useCallback(
    async (targetView: NativeAuthView, loginHint?: string) => {
      setState({ phase: "redirecting" });
      try {
        // Always the standard instance: the native client's authority cannot resolve the
        // endpoints a redirect needs (endpoints_resolution_error).
        const redirectClient = getMsalInstance();
        if (instance !== redirectClient) {
          console.warn("MsalProvider holds a different instance than the redirect client.");
        }
        await startHostedSignIn(redirectClient, { view: targetView, loginHint, returnTo });
      } catch (error) {
        console.error("Hosted sign-in could not start", error);
        setState({
          phase: "error",
          message:
            error instanceof RedirectLoopError
              ? "We couldn't complete sign-in. Please try again."
              : "We couldn't open the sign-in page. Check your connection and try again.",
        });
      }
    },
    [instance, returnTo]
  );

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    let active = true;

    const start = async () => {
      if (consumeRedirectError()) {
        setState({ phase: "error", message: "Sign-in didn't complete. Please try again." });
        return;
      }

      const client = getNativeAuthClient();
      if (!client) {
        await redirectToHostedSignIn(view);
        return;
      }

      // The sign-in pages do use the remembered slug: it has to survive the OTP and reset steps.
      const resolution = await resolveAffiliateOnce(getAffiliateSlug());
      if (!active) {
        return;
      }

      if (resolution.kind === "found") {
        setState({ phase: "native", affiliate: resolution.affiliate, client });
        return;
      }

      if (resolution.reason !== "skipped" && resolution.reason !== "not-found") {
        console.warn(`Affiliate branding unavailable (${resolution.reason}); using hosted sign-in.`);
      }
      await redirectToHostedSignIn(view);
    };

    void start();

    return () => {
      active = false;
    };
  }, [attempt, inProgress, redirectToHostedSignIn, view]);

  useEffect(() => {
    if (state.phase !== "redirecting") {
      setShowContinue(false);
      return;
    }
    const timer = setTimeout(() => setShowContinue(true), CONTINUE_BUTTON_DELAY_MS);
    return () => clearTimeout(timer);
  }, [state.phase]);

  const affiliate = state.phase === "native" ? state.affiliate : null;
  useDocumentBranding(affiliate?.theme ?? null, affiliate ? `Sign in · ${affiliate.name}` : undefined);

  const affiliateTheme = useMemo(
    () => (affiliate ? createAffiliateTheme(baseTheme, affiliate.theme, { scope: "full" }) : null),
    [affiliate, baseTheme]
  );

  const tryAgain = () => {
    resetRedirectLoopGuard();
    clearAffiliateResolution();
    setState({ phase: "resolving" });
    setAttempt((current) => current + 1);
  };

  switch (state.phase) {
    case "resolving":
      return <AuthStatusScreen message="Loading sign-in…" />;

    case "redirecting":
      return (
        <AuthStatusScreen message="Redirecting to secure sign-in…">
          {showContinue && (
            <Button variant="outlined" onClick={tryAgain}>
              Continue to sign in
            </Button>
          )}
        </AuthStatusScreen>
      );

    case "error":
      return <AuthErrorScreen message={state.message} actionLabel="Try again" onAction={tryAgain} />;

    case "native":
      return (
        <ThemeProvider theme={affiliateTheme!}>
          <NativeAuthFlow
            client={state.client}
            affiliate={state.affiliate}
            initialView={view}
            returnTo={returnTo}
            onRedirectRequired={(targetView, loginHint) => void redirectToHostedSignIn(targetView, loginHint)}
          />
        </ThemeProvider>
      );
  }
}
